// One frame for every route. Every page — list, board, detail, settings — gets the
// same outer width and padding, so the left edge of the text never moves as you
// navigate. Pages that were individually max-w-3xl / 4xl / 5xl were the reason it
// felt unsteady.
//
// Two shapes live here:
//
//   `View`  — a data view. Its only chrome is the VIEW BAR: the lenses that narrow
//             it, a filter, and the New button. No page title, because the tab in
//             the header already names the thing you're looking at; repeating it
//             cost 60px of every screen to say something already on screen.
//   `Page`  — a document. Detail pages and Settings, which do have their own titles
//             because you arrived at one specific record.
//
// `aside` opts into the two-column shape used by the detail pages: the main column
// carries what you read (the timeline first), the aside carries the facts. It
// collapses to one column on narrow screens, aside last.

import { useEffect, useRef, type ReactNode } from 'react'
import { NavLink } from 'react-router-dom'

import { colorVar } from '../lib/model'
import { Icon, type IconName } from './Icon'

/** The shared measure. Wide enough for a detail page's two columns to breathe. */
const FRAME = 'mx-auto w-full max-w-[1180px] px-5 sm:px-8'

/**
 * Attach to whatever sticky header sits at the top of a scroller: it publishes the
 * element's live height as `--page-header-h`, which is what everything else sticking
 * below it (table heads, detail asides) parks against. Measuring beats a constant —
 * the bar wraps to two rows in a narrow window, and a stale number there leaves the
 * column headers floating in the middle of the table.
 */
export function useStickyHeight<T extends HTMLElement>() {
  const ref = useRef<T | null>(null)
  useEffect(() => {
    const node = ref.current
    if (!node) return
    const publish = () =>
      document.documentElement.style.setProperty(
        '--page-header-h',
        `${Math.round(node.getBoundingClientRect().height)}px`,
      )
    publish()
    const ro = new ResizeObserver(publish)
    ro.observe(node)
    return () => ro.disconnect()
  }, [])
  return ref
}

/**
 * A lens: one chip that narrows the view. Either a route (linkable, back button
 * works — which is what the work kinds want) or a local toggle.
 */
export interface Lens {
  key: string
  label: string
  count?: number | undefined
  /** a vocabulary colour name, so a lens chip matches the pills in its column */
  color?: string | undefined
  to?: string | undefined
}

/**
 * A data view: view bar, then the sheet. Every list in the app is this, which is
 * the whole point — switching views should change the columns, not the furniture.
 */
export function View({
  lenses,
  active,
  onLens,
  hint,
  actions,
  children,
  full = false,
}: {
  lenses?: Lens[] | undefined
  active?: string | undefined
  onLens?: ((key: string) => void) | undefined
  /** one short line, in the bar. Long explanations belong in the docs, not the chrome. */
  hint?: string | undefined
  actions?: ReactNode
  children: ReactNode
  /** fill the rest of the scroller instead of flowing: for the canvas view */
  full?: boolean
}) {
  const bare = !lenses?.length && !hint && !actions
  return (
    <>
      {bare ? (
        <NoStickyHeader />
      ) : (
        <ViewBar lenses={lenses} active={active} onLens={onLens} hint={hint} actions={actions} />
      )}
      {full ? (
        // Fills whatever the bar left behind. The canvas has to know its own height in
        // pixels, so this can't be a flowing block.
        <div className="relative" style={{ height: 'calc(100% - var(--page-header-h))' }}>
          {children}
        </div>
      ) : (
        /* A container, so the sheet's columns respond to the space they actually have
           rather than to the window: opening the peek takes 400px away, and a
           viewport-based breakpoint would happily let the last column slide under it. */
        <div
          className={`@container ${FRAME} pb-16 ${bare ? 'pt-6' : 'pt-4'}`}
          style={{ overflowAnchor: 'none' }}
        >
          {children}
        </div>
      )}
    </>
  )
}

/** A view with nothing to put in its bar has no bar, and nothing to stick beneath. */
function NoStickyHeader() {
  useEffect(() => {
    document.documentElement.style.setProperty('--page-header-h', '0px')
  }, [])
  return null
}

/**
 * The view bar. Sticky at the top of the scroller, and it publishes its own height as
 * `--page-header-h` so the table head can park directly beneath it. Measuring beats
 * hard-coding: the bar wraps to two rows on a narrow window, and a stale constant
 * there leaves the column headers floating in the middle of the table.
 */
function ViewBar({
  lenses,
  active,
  onLens,
  hint,
  actions,
}: {
  lenses?: Lens[] | undefined
  active?: string | undefined
  onLens?: ((key: string) => void) | undefined
  hint?: string | undefined
  actions?: ReactNode
}) {
  const el = useStickyHeight<HTMLDivElement>()

  return (
    <div
      ref={el}
      className="sticky top-0 z-30 border-b"
      style={{ background: 'var(--bg)', borderColor: 'var(--border)' }}
    >
      <div className={`${FRAME} flex flex-wrap items-center gap-x-2.5 gap-y-2 py-2`}>
        {lenses?.map((lens) => (
          <LensChip key={lens.key} lens={lens} active={lens.key === active} onPick={onLens} />
        ))}
        {hint && <span className="meta hidden truncate sm:inline">{hint}</span>}
        <span className="flex-1" />
        {actions}
      </div>
    </div>
  )
}

/**
 * A chip that narrows what a view shows.
 *
 * **This is the app's only chip.** There were five other shapes doing this job —
 * the board's All/Open/Done at 11.5px with a rail fill, Activity's filters at
 * 12.5px in an 8px box, the header tabs, the client page's sub-tabs and Settings'
 * nav — and the split that actually matters is: a TAB changes what the page IS, a
 * CHIP narrows what it SHOWS. Tabs are underlined; everything that narrows is this.
 *
 * Exported because two of those chip rows do not live in a view bar.
 */
export function LensChip({
  lens,
  active,
  onPick,
}: {
  lens: Lens
  active: boolean
  onPick?: ((key: string) => void) | undefined
}) {
  const hue = lens.color ? `var(${colorVar(lens.color)})` : 'var(--fg)'
  const style = (on: boolean) => ({
    fontSize: 'var(--text-meta)',
    fontWeight: on ? 560 : 400,
    color: on ? hue : 'var(--muted)',
    background: on ? `color-mix(in srgb, ${hue} 12%, transparent)` : 'transparent',
    border: `1px solid ${on ? `color-mix(in srgb, ${hue} 30%, transparent)` : 'var(--border)'}`,
  })
  const body = (on: boolean) => (
    <>
      {lens.label}
      {lens.count != null && (
        <span
          style={{
            fontFamily: 'var(--mono)',
            fontSize: 'var(--text-micro)',
            color: on ? 'inherit' : 'var(--faint)',
          }}
        >
          {lens.count}
        </span>
      )}
    </>
  )
  const cls = 'inline-flex items-center gap-1.5 rounded-full px-2.5 py-[3px] whitespace-nowrap'

  if (lens.to) {
    return (
      <NavLink to={lens.to} end className={cls} style={({ isActive }) => style(isActive)}>
        {({ isActive }) => body(isActive)}
      </NavLink>
    )
  }
  return (
    <button type="button" onClick={() => onPick?.(lens.key)} className={cls} style={style(active)}>
      {body(active)}
    </button>
  )
}

export function Page({
  eyebrow,
  eyebrowIcon,
  crumbs,
  title,
  sub,
  right,
  banner,
  aside,
  children,
  /** boards manage their own scrolling and want the frame without the vertical rhythm */
  flush = false,
}: {
  eyebrow?: string | undefined
  eyebrowIcon?: IconName | undefined
  /** Where this page sits. A detail page with no way out but the back button is a
   *  dead end, and the back button is not navigation — it is undo. */
  crumbs?: Array<{ label: string; to: string }> | undefined
  /** a node, not a string: a record page's title is its name, and a name is
   *  editable wherever the record itself is */
  title?: ReactNode
  sub?: string | undefined
  right?: ReactNode
  /**
   * Full-width content above the columns. A detail page's header belongs to the whole
   * page, not to the main column — putting it inside the split made the title stop
   * short of the aside and the page read as two unrelated halves.
   */
  banner?: ReactNode
  aside?: ReactNode
  children: ReactNode
  flush?: boolean
}) {
  return (
    <div
      className={`${FRAME} ${flush ? 'flex h-full flex-col py-6 sm:py-7' : 'py-6 sm:py-7'}`}
      style={{ overflowAnchor: 'none' }}
    >
      {(title || right) && (
        <PageHeader
          eyebrow={eyebrow}
          eyebrowIcon={eyebrowIcon}
          crumbs={crumbs}
          title={title}
          right={right}
        />
      )}

      {/* The explanatory line is CONTENT, not chrome: it belongs in the scroll, where
          it can leave once you've read it, instead of costing height in a header that
          never moves. */}
      {sub && (
        <p className="text-body mb-6 max-w-[68ch]" style={{ color: 'var(--muted)' }}>
          {sub}
        </p>
      )}

      {banner}

      {aside ? (
        <div className="flex flex-col gap-8 lg:flex-row lg:gap-10">
          <main className="min-w-0 flex-1">{children}</main>
          <aside
            // Sticks BELOW the page header, not under it — the header is sticky too, and
            // whichever one is higher just covers the other.
            className="w-full shrink-0 lg:sticky lg:w-[300px] lg:self-start"
            style={{ top: 'calc(var(--page-header-h) + 0.5rem)' }}
          >
            {aside}
          </aside>
        </div>
      ) : (
        children
      )}
    </div>
  )
}

/**
 * The page header: dense, sticky, and unchanging. It used to condense on scroll, which
 * meant a layout change mid-scroll — and any layout change at a scroll threshold reads
 * as a click into place no matter how carefully it's damped. So instead it's dense
 * enough to keep permanently, and it simply stays. The explanatory sub-line moved into
 * the content, which is what made room for that.
 */
function PageHeader({
  eyebrow,
  eyebrowIcon,
  crumbs,
  title,
  right,
}: {
  eyebrow?: string | undefined
  /** A record page wears its kind's icon beside the eyebrow. A page that names a
   *  kind and omits it reads as a different sort of screen. */
  eyebrowIcon?: IconName | undefined
  crumbs?: Array<{ label: string; to: string }> | undefined
  title?: ReactNode
  right?: ReactNode
}) {
  const box = useStickyHeight<HTMLElement>()
  return (
    <header
      ref={box}
      className="sticky top-0 z-20 mb-5 border-b pt-1"
      style={{ background: 'var(--bg)', borderColor: 'var(--border)' }}
    >
      {/* Stacked — eyebrow, then name — but with the air squeezed out of it rather
          than the tiers collapsed into one line. Density came from the spacing, not
          from stripping the hierarchy. */}
      <div className="flex flex-wrap items-end gap-x-4 gap-y-1 pb-2">
        <div className="min-w-0 flex-1">
          {/* Where you are, then what this is. The kind used to come first and the
              trail was appended after a slash, which read as though the kind
              contained the client — and on the client page itself it rendered the
              nonsense "CLIENT / CLIENTS". The trail is the sentence; the kind is a
              label on the end of it. */}
          {(eyebrow || crumbs?.length) && (
            <div className="eyebrow flex flex-wrap items-center gap-1.5 leading-none">
              {crumbs?.map((c, i) => (
                <span key={c.to} className="flex items-center gap-1.5">
                  {i > 0 && <span style={{ color: 'var(--faint)' }}>/</span>}
                  <NavLink to={c.to} style={{ color: 'var(--accent)' }}>
                    {c.label}
                  </NavLink>
                </span>
              ))}
              {eyebrow && crumbs?.length ? <span style={{ color: 'var(--faint)' }}>·</span> : null}
              {eyebrowIcon && <Icon name={eyebrowIcon} size={12} />}
              {eyebrow}
            </div>
          )}
          {title && (
            <h1
              className="text-page mt-1 font-semibold tracking-tight"
              style={{ textWrap: 'balance', lineHeight: 1.15 }}
            >
              {title}
            </h1>
          )}
        </div>
        {right && <span className="flex shrink-0 flex-wrap items-center gap-2">{right}</span>}
      </div>
    </header>
  )
}

export function FilterInput({
  value,
  onChange,
  placeholder = 'Filter…',
}: {
  value: string
  onChange: (v: string) => void
  placeholder?: string
}) {
  return (
    <span className="relative">
      <Icon
        name="Search"
        size={14}
        className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2"
        style={{ color: 'var(--faint)' }}
      />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="rounded-control text-body w-40 border py-1 pr-2 pl-7 outline-none"
        style={{ borderColor: 'var(--border)', background: 'var(--panel)' }}
      />
    </span>
  )
}

/**
 * A labelled block in the aside: a small caps label over its value. This is the
 * unit that makes the detail pages parseable — facts stop competing with prose for
 * the reader's attention because they're physically somewhere else.
 */
export function Fact({
  label,
  children,
  hint,
}: {
  label: string
  children: ReactNode
  hint?: string | undefined
}) {
  return (
    <div className="flex flex-col gap-0.5 py-2">
      <span className="eyebrow">{label}</span>
      <div className="text-body">{children}</div>
      {hint && <span className="meta">{hint}</span>}
    </div>
  )
}

/** A titled group inside the aside, separated by a hairline rather than a box. */
export function AsideBlock({
  title,
  right,
  children,
}: {
  title?: string | undefined
  right?: ReactNode
  children: ReactNode
}) {
  return (
    <section
      className="mb-5 border-t pt-3 first:border-t-0 first:pt-0"
      style={{ borderColor: 'var(--border)' }}
    >
      {title && (
        <div className="mb-1.5 flex items-baseline gap-2">
          <h2 className="eyebrow">{title}</h2>
          {right && <span className="ml-auto">{right}</span>}
        </div>
      )}
      {children}
    </section>
  )
}

/* `FactStrip` stood here, with `StripFact`: a row of derived numbers with a
   sub-line and linkable cells, and no caller anywhere. `Stats` in common.tsx is the
   same object, is the one the app draws, and has absorbed both of those features.
   A dead primitive is worse than none: it is where the next divergence starts. */

/**
 * A second tier of tabs, inside one record's page.
 *
 * Only the client page has enough distinct material to earn these. It was one long
 * scroll of five sections, four of which vanish when empty, so the page had no
 * stable shape — tabs give it one, and the header and fact strip stay put above
 * them so switching never feels like changing page.
 *
 * Local state, not routes: this is a lens on one record, and burning a URL segment
 * on it would put the section above the record in the path, which is backwards.
 */
export interface SubTab {
  key: string
  label: string
  count?: number | undefined
  /** pushed to the right and given its own accent — for the client's own view */
  apart?: boolean | undefined
}

export function SubTabs({
  tabs,
  active,
  onPick,
}: {
  tabs: SubTab[]
  active: string
  onPick: (key: string) => void
}) {
  return (
    <div
      className="mb-5 flex flex-wrap items-baseline gap-x-5 border-b"
      style={{ borderColor: 'var(--border)' }}
      role="tablist"
    >
      {tabs.map((t) => {
        const on = t.key === active
        const hue = t.apart ? 'var(--marine)' : 'var(--c-violet)'
        return (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={on}
            onClick={() => onPick(t.key)}
            className={`text-body -mb-px shrink-0 pb-1.5 ${t.apart ? 'ml-auto' : ''}`}
            style={{
              color: on ? 'var(--fg)' : t.apart ? 'var(--marine)' : 'var(--muted)',
              fontWeight: on ? 620 : t.apart ? 540 : 450,
              borderBottom: `2px solid ${on ? hue : 'transparent'}`,
            }}
          >
            {t.label}
            {t.count !== undefined && t.count > 0 && (
              <span
                className="ml-1"
                style={{
                  fontFamily: 'var(--mono)',
                  fontSize: 'var(--text-micro)',
                  color: 'var(--faint)',
                }}
              >
                {t.count}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}
